import React from 'react'
import { cn } from 'components/UI/cn'
import { Star, Upload, Trash2, Image } from 'lucide-react'
import type { VariantImage } from '../../../../services/products_api'

interface VariantImageCardProps {
  image: VariantImage
  onSetMain: (documentId: number) => void
  onChangeImage?: (documentId: number, file: File) => void
  onRemoveImage?: (documentId: number) => void
  canSetMain: boolean
  isSettingMain: boolean
  isChanging?: boolean
  isRemoving?: boolean
  /** If true, hide change/remove actions (e.g., for Syndigo-synced images) */
  disableActions?: boolean
}

export function VariantImageCard({
  image,
  onSetMain,
  onChangeImage,
  onRemoveImage,
  canSetMain,
  isSettingMain,
  isChanging = false,
  isRemoving = false,
  disableActions = false,
}: VariantImageCardProps) {
  const fileInputRef = React.useRef<HTMLInputElement>(null)

  const handleStarClick = () => {
    if (image.is_main || !canSetMain || isSettingMain) return
    onSetMain(image.id)
  }

  const handleOpenImage = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    if (image.file) {
      window.open(image.file, '_blank')
    }
  }

  const handleChangeClick = () => {
    fileInputRef.current?.click()
  }

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file || !onChangeImage) return
    onChangeImage(image.id, file)
    event.target.value = ''
  }

  const handleRemoveClick = () => {
    if (onRemoveImage) {
      onRemoveImage(image.id)
    }
  }

  // Get file name without extension and extension separately
  const getFileNameParts = (name: string): [string, string] => {
    const parts = name.split('.')
    if (parts.length === 1) return [name, '']
    const ext = parts.pop() || ''
    const nameWithoutExt = parts.join('.')
    return [nameWithoutExt, ext]
  }

  const [nameWithoutExt, ext] = getFileNameParts(
    image.name || image.original_name
  )

  const showActions = !disableActions && (onChangeImage || onRemoveImage)

  return (
    <div
      className={cn(
        'relative border border-line-form rounded-xl w-[280px] flex items-center justify-center flex-col bg-white',
        {
          'border-line-focus shadow-sm shadow-[#8EA69B]/50': image.is_main,
        }
      )}
    >
      {/* Hidden file input for change functionality */}
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileChange}
        accept=".jpg,.jpeg,.png,.svg,.gif,.webp"
        className="hidden"
      />

      {/* Star button for main image */}
      {canSetMain && (
        <Star
          onClick={handleStarClick}
          className={cn('absolute top-2 left-2 w-5 h-5 cursor-pointer', {
            'text-yellow-500 fill-yellow-500': image.is_main,
            'text-gray-300': !image.is_main,
          })}
        />
      )}

      {/* Image preview area with file name */}
      <div
        className="flex items-center justify-center flex-col w-full h-[118px] border-b border-line-form cursor-pointer hover:bg-gray-50"
        onClick={handleOpenImage}
        title={image.name || image.original_name}
      >
        {image.file ? (
          <img
            className="w-20 h-20 object-cover"
            src={image.file}
            alt={image.name}
          />
        ) : (
          <div className="text-gray-400 text-sm">No preview</div>
        )}
        <div className="text-sm text-gray-900 mt-2 flex min-w-0 max-w-44">
          <span className="truncate">{nameWithoutExt}</span>
          {ext && <span>.{ext}</span>}
        </div>
      </div>

      {/* Action buttons - Change and Remove (like LogoCard) */}
      {showActions ? (
        <div className="flex items-center justify-around w-full h-[52px] text-gray-600">
          <button
            type="button"
            className="flex h-full w-full items-center justify-center border-r border-line-form cursor-pointer hover:bg-gray-50 rounded-bl-xl"
            onClick={handleChangeClick}
            disabled={isChanging || disableActions}
          >
            <Upload className="mr-2 w-5 h-5" />
            <span className="text-sm">
              {isChanging ? 'Updating...' : 'Change'}
            </span>
          </button>
          <button
            type="button"
            className="flex h-full w-full items-center justify-center cursor-pointer hover:bg-gray-50 rounded-br-xl"
            onClick={handleRemoveClick}
            disabled={isRemoving || disableActions}
          >
            <Trash2 className="mr-2 w-5 h-5" />
            <span className="text-sm">
              {isRemoving ? 'Removing...' : 'Remove'}
            </span>
          </button>
        </div>
      ) : (
        <div className="flex items-center justify-center w-full h-[52px] text-gray-500 text-sm">
          Managed by Syndigo
        </div>
      )}
    </div>
  )
}

interface AddImageCardProps {
  onAddImage: (file: File) => void
  isUploading?: boolean
}

export function AddImageCard({
  onAddImage,
  isUploading = false,
}: AddImageCardProps) {
  const fileInputRef = React.useRef<HTMLInputElement>(null)

  const handleAddClick = () => {
    fileInputRef.current?.click()
  }

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return
    onAddImage(file)
    event.target.value = ''
  }

  return (
    <div
      className="cursor-pointer border border-line-form rounded-xl w-[280px] flex items-center justify-center flex-col hover:border-line-focus transition-colors bg-white"
      onClick={handleAddClick}
    >
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileChange}
        accept=".jpg,.jpeg,.png,.svg,.gif,.webp"
        className="hidden"
      />

      <div className="flex items-center justify-center w-full h-[118px] border-b border-line-form">
        <div className="bg-line-form w-20 h-20 flex items-center justify-center">
          <Image className="text-gray-900 w-6 h-6" />
        </div>
      </div>
      <div className="inline-flex items-center justify-center w-full h-[52px] text-gray-600">
        <Upload className="mr-2 w-5 h-5" />
        <span className="text-sm">
          {isUploading ? 'Uploading...' : 'Add Image'}
        </span>
      </div>
    </div>
  )
}
